import React from 'react';
import {
  TranscriptionTextField,
  TranscriptionTextTags,
} from '@scale/shared/types/params/textcollection';
import TranscriptionTextDropdown, { DropdownInfo } from './TranscriptionTextDropdown';
import {
  TranscriptionTextFieldAnnotation,
  TranscriptionTextAnnotationTags,
} from '@scale/shared/types/response/textcollection';
import { makeStyles } from 'design-system/styles';
import { Editor, EditorState, ContentState, Modifier } from 'draft-js';
import Immutable from 'immutable';
interface TranscriptionTextFieldSectionProps {
  field: TranscriptionTextField;
  annotation: TranscriptionTextFieldAnnotation;
  setAnnotation: (annotation: TranscriptionTextFieldAnnotation) => void;
  disabled?: boolean;
}

const EDITOR_PADDING = 10;
const EDITOR_FONT_SIZE = 14;

export default function TranscriptionTextFieldSection(props: TranscriptionTextFieldSectionProps) {
  const editorRef = React.useRef<HTMLDivElement>(null);
  const styles = useTranscriptionTextFieldSectionStyles(props);
  const [editorState, setEditorState] = React.useState(() =>
    EditorState.createWithContent(ContentState.createFromText(props.annotation.response[0])),
  );

  const [openTagDropdown, setOpenTagDropdown] = React.useState<boolean>(false);
  const [dropdownInfo, setDropdownInfo] = React.useState<DropdownInfo | undefined>(undefined);

  const tags: TranscriptionTextTags = props.field.tags ?? {};
  const specialCharacters: string[] = Object.keys(tags);

  const isSelectingRange = () =>
    editorState.getSelection().getAnchorOffset() !== editorState.getSelection().getFocusOffset();

  React.useEffect(
    () => {
      const plainText = editorState.getCurrentContent().getPlainText();
      if (plainText.length > 0 && !isSelectingRange()) {
        const cursorPosition = editorState.getSelection().getAnchorOffset();
        const lastCharacter = plainText.slice(cursorPosition - 1, cursorPosition);

        if (specialCharacters.includes(lastCharacter) && tags[lastCharacter] && editorRef.current) {
          const { top, left, width } = editorRef.current.getBoundingClientRect();

          const canvas = document.createElement('canvas');
          const context = canvas?.getContext('2d');
          if (!context) {
            setOpenTagDropdown(false);
            setDropdownInfo(undefined);
            return;
          }

          context.font = getComputedStyle(editorRef.current).font;
          const textWidth = context.measureText(plainText).width;
          const numLines =
            Math.ceil(textWidth / (width - 2 * EDITOR_PADDING)) +
            (plainText.match(/\n/g) ?? []).length +
            3; // 3 is just a random offset to create additional buffer between text and dropdown

          setDropdownInfo({
            ...tags[lastCharacter],
            cursorPos: {
              x: left + EDITOR_PADDING,
              // 2 is just an additional offset per line
              y: top + numLines * (EDITOR_FONT_SIZE + 2),
            },
          });
          setOpenTagDropdown(true);
          return;
        }
      }
      setOpenTagDropdown(false);
      setDropdownInfo(undefined);
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editorState],
  );

  const updateAnnotation = function (e: EditorState): void {
    const tagsForAnnotation: TranscriptionTextAnnotationTags = Object.fromEntries(
      specialCharacters.map(character => [character, tags[character]?.followUpCharacter]),
    );
    const annotation: TranscriptionTextFieldAnnotation = {
      ...props.annotation,
      response: [e.getCurrentContent().getPlainText()],
      tags: tagsForAnnotation,
    };

    props.setAnnotation(annotation);
  };

  const handleDelete = function (): EditorState {
    const selectionState = editorState.getSelection();
    const plainText = editorState.getCurrentContent().getPlainText();
    const toRemoveSelection = selectionState.merge({
      focusOffset: plainText.length,
      anchorOffset: plainText.length - 1,
    });
    const newContentState = Modifier.replaceText(
      editorState.getCurrentContent(),
      toRemoveSelection,
      '',
      Immutable.OrderedSet(['REPLACE']),
    );
    const nextState = EditorState.moveFocusToEnd(
      EditorState.push(editorState, newContentState, 'delete-character'),
    );
    setEditorState(nextState);
    return nextState;
  };

  const createAutocompleteText = (
    inputs: string[],
    followUpCharacter: string,
    inputIndex: number,
  ) => {
    const input = inputs[inputIndex];
    if (inputs.length === 1) {
      return input + followUpCharacter;
    }
    if (inputIndex === 0) {
      return '{' + input + ', ';
    }
    if (inputIndex === inputs.length - 1) {
      return input + '}';
    }
    return input + ', ';
  };

  const handleAutocomplete = function (inputs: string[], followUpCharacter: string): void {
    // if there is more than one input, we need to delete the previos character (which is a hard bracket) and
    // replace with a curly brace
    let nextEditorState = inputs.length > 1 ? handleDelete() : editorState;
    for (let inputIndex = 0; inputIndex < inputs.length; inputIndex++) {
      const contentState = nextEditorState.getCurrentContent();
      const selectionState = nextEditorState.getSelection();
      const contentStateWithEntity = contentState.createEntity('TOKEN', 'IMMUTABLE', {
        time: new Date().getTime(),
      });
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
      const text = createAutocompleteText(inputs, followUpCharacter, inputIndex);

      const modifiedContent = Modifier.insertText(
        contentState,
        selectionState,
        text,
        Immutable.OrderedSet(['INSERT']),
        entityKey,
      );
      nextEditorState = EditorState.push(
        nextEditorState,
        modifiedContent,
        nextEditorState.getLastChangeType(),
      );
    }

    setEditorState(EditorState.moveFocusToEnd(nextEditorState));
  };

  const requiredStr = props.field.required ? ' *' : '';
  const title = `${props.field.title}${requiredStr}`;

  // NOTE: there should only ever be one transcription text field
  if (props.annotation.response.length !== 1) {
    return null;
  }
  return (
    <>
      <div key={`${props.field.field_id}`} className={styles.wrapper}>
        <div>{title}</div>

        <div className={styles.editor} ref={editorRef}>
          <Editor
            key="editor"
            editorState={editorState}
            onChange={(e: EditorState) => {
              updateAnnotation(e);
              setEditorState(e);
            }}
            spellCheck={props.field.spellcheck}
          />
        </div>
        {openTagDropdown && dropdownInfo && (
          <TranscriptionTextDropdown
            dropdownInfo={dropdownInfo}
            setDropdownInfo={setDropdownInfo}
            openTagDropdown={openTagDropdown}
            setOpenTagDropdown={setOpenTagDropdown}
            handleAutocomplete={handleAutocomplete}
            handleDelete={handleDelete}
            spellcheck={props.field.spellcheck}
          />
        )}
      </div>
    </>
  );
}

const useTranscriptionTextFieldSectionStyles = makeStyles(() => {
  return {
    wrapper: { EDITOR_PADDING },
    editor: {
      'border-radius': '12px',
      height: '500px',
      padding: EDITOR_PADDING,
      fontSize: EDITOR_FONT_SIZE,
      overflow: 'scroll',
      'border-color': '#e3e3e3',
      'border-style': 'solid',
      'border-width': '1px',
    },
  };
});
